import { Box, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import {
    DotLegalBreadCrumbModel,
    DotLegalButton,
    DotLegalEmptyState,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { RiskConsequenceViewModel } from "./RiskConsequences.types";
import { toSaveModel, useRiskConsequencesDataMapping } from "./RiskConsequences.hooks";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useRiskConsequenceStyles } from "./RiskConsequences.styles";
import RiskConsequenceDialog from "./riskConsequenceDialog/RiskConsequenceDialog";
import RiskConsequenceCatalogDialog from "./riskConsequenceCatalogDialog/RiskConsequenceCatalogDialog";
import { DeleteRiskConsequenceDialog } from "./deleteRiskConsequenceDialog/DeleteRiskConsequenceDialog";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import Rocket from "../../processingActivity/processingActivities/rocket.svg?react";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function RiskConsequences() {
    const styles = useRiskConsequenceStyles();
    const { translateString, translateRiskConsequenceCategory } = useTranslation();
    const { permissions } = useUserContext();
    const breadCrumbs: Array<DotLegalBreadCrumbModel> = [{ name: translateString("riskConsequences") }];

    const {
        isLoading,
        data,
        hasRiskConsequences,
        hasCatalogItemsToImport,
        showRiskConsequenceDialog,
        setShowRiskConsequenceDialog,
        showCatalogDialog,
        setShowCatalogDialog,
        riskConsequenceForDeletion,
        setRiskConsequenceForDeletion,
        onSave,
        selectedRiskConsequence,
        setSelectedRiskConsequence,
        getFilterDataOptions,
        mapSearchFilter,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useRiskConsequencesDataMapping();

    const getMenuItems = (riskConsequence: RiskConsequenceViewModel) => {
        let menuItems = [];

        if (permissions.canManageMasterData) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setSelectedRiskConsequence(toSaveModel(riskConsequence));
                    setShowRiskConsequenceDialog(true);
                },
            });

            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setRiskConsequenceForDeletion(riskConsequence);
                },
            });
        }

        return menuItems;
    };

    const openCreateDialog = () => {
        setSelectedRiskConsequence(undefined);
        setShowRiskConsequenceDialog(true);
    };

    return (
        <Box sx={styles.container}>
            <DotLegalPageHeader
                breadCrumbs={breadCrumbs}
                rightComponent={
                    permissions.canManageMasterData ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                            {hasCatalogItemsToImport && (
                                <DotLegalButton buttonType={"secondary"} size="medium" onClick={() => setShowCatalogDialog(true)}>
                                    {translateString("riskConsequenceCatalog")}
                                </DotLegalButton>
                            )}
                            <DotLegalButton buttonType={"primary"} size="medium" onClick={openCreateDialog}>
                                {translateString("createRiskConsequence")}
                            </DotLegalButton>
                        </Box>
                    ) : undefined
                }
            />

            <TablePagePaper>
                {hasRiskConsequences ? (
                    <DotLegalTableWithFilterBar
                        internalScroll
                        filterBar={{
                            isLoading: isLoading,
                            filterData: getFilterDataOptions(),
                            fetchFilterData: true,
                            onMenuClose: (options) => mapSearchFilter(options),
                            onRemove: (options) => mapSearchFilter(options),
                            pinnedFilterIds: pinnedFilterIds,
                            onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                        }}
                        headers={getHeaders(translateString)}
                        rowsPerPage={30}
                        hasOverflowMenu
                        defaultOrderBy={"name"}
                        defaultOrder={"asc"}
                        isLoading={isLoading}
                        clickableRows={permissions.canManageMasterData}
                        emptyText={translateString("noRiskConsequencesMatchesQuery")}
                        data={data}
                        renderRow={(row) => (
                            <TableRow
                                onClick={
                                    permissions.canManageMasterData
                                        ? () => {
                                              setSelectedRiskConsequence(toSaveModel(row));
                                              setShowRiskConsequenceDialog(true);
                                          }
                                        : undefined
                                }
                                key={row.id}
                            >
                                <TableCell sx={styles.bold}>{row.name}</TableCell>
                                <TableCell>{row.description}</TableCell>
                                <TableCell>{translateRiskConsequenceCategory(row.category)}</TableCell>
                                <TableCell>
                                    {row.appliesToAllRiskAreas ? translateString("allRiskAreas") : row.riskAreas.map((riskArea) => riskArea.name).join(", ")}
                                </TableCell>
                                <TableCell align={"right"}>
                                    <DotLegalOverflowMenu items={getMenuItems(row)} />
                                </TableCell>
                            </TableRow>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState icon={<Rocket />} text={translateString("createRiskConsequenceToGetStarted")} />
                )}
            </TablePagePaper>

            {showRiskConsequenceDialog && (
                <RiskConsequenceDialog
                    onHideDialog={() => setShowRiskConsequenceDialog(false)}
                    onSave={onSave}
                    selectedRiskConsequence={selectedRiskConsequence}
                />
            )}

            {riskConsequenceForDeletion && (
                <DeleteRiskConsequenceDialog
                    selectedRiskConsequence={riskConsequenceForDeletion}
                    onDeletion={onSave}
                    onCloseDialog={() => setRiskConsequenceForDeletion(undefined)}
                />
            )}

            {showCatalogDialog && <RiskConsequenceCatalogDialog onHideDialog={() => setShowCatalogDialog(false)} onImported={onSave} />}
        </Box>
    );
}

export default RiskConsequences;

function getHeaders(translateString: (key: string) => string) {
    let headers = Array<ITableHeader<RiskConsequenceViewModel>>();
    headers.push({ property: "name", text: translateString("riskConsequence"), align: "left", showOnMobile: true });
    headers.push({ property: "description", text: translateString("description"), align: "left", showOnMobile: true });
    headers.push({ property: "category", text: translateString("category"), align: "left", showOnMobile: true });
    headers.push({ property: "riskAreas", text: translateString("riskAreas"), align: "left", showOnMobile: true });

    return headers;
}
